import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useNFCContext } from '../contexts/NFCContext';
import { useTransactionContext } from '../contexts/TransactionContext';
import { formatCurrency } from '../utils/formatters';
import { toast } from 'react-toastify';
import './Recharge.css';

const NFC_STATES = {
  IDLE: 'idle',
  SCANNING: 'scanning',
  SUCCESS: 'success',
  ERROR: 'error'
};

const RECHARGE_AMOUNTS = [
  { value: 5000, label: '$5.000' },
  { value: 10000, label: '$10.000' },
  { value: 20000, label: '$20.000' },
  { value: 30000, label: '$30.000' },
  { value: 50000, label: '$50.000' },
  { value: 100000, label: '$100.000' }
];

const Recharge = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { startScan, writeNFC, nfcData: contextNfcData, nfcState: contextNfcState, reset } = useNFCContext();
  const { createTransaction } = useTransactionContext();

  const [nfcState, setNfcState] = useState(NFC_STATES.IDLE);
  const [nfcData, setNfcData] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [useCustomAmount, setUseCustomAmount] = useState(false);

  const MIN_RECHARGE = 1000;
  const MAX_RECHARGE = 500000;
  const MAX_BALANCE = 1000000;

  const handleScanCard = async () => {
    try {
      setNfcState(NFC_STATES.SCANNING);
      
      // Iniciar escaneo NFC
      await startScan();
      
      // Simular delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Usar datos del contexto NFC
      const scannedData = contextNfcData || { id: '123', nombre: 'Estudiante Demo', grado: '10°', saldo: 25000 };
      setNfcData(scannedData);
      setNfcState(NFC_STATES.SUCCESS);
      
      toast.success(`Carnet de ${scannedData.nombre} escaneado`, {
        position: "top-center",
        autoClose: 2000
      });
    } catch (error) {
      console.error('Error reading NFC:', error);
      setNfcState(NFC_STATES.ERROR);
      toast.error('Error al leer el carnet NFC', {
        position: "top-center"
      });
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setUseCustomAmount(false);
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setCustomAmount(value);
    if (value) {
      setSelectedAmount(parseInt(value));
      setUseCustomAmount(true);
    } else {
      setSelectedAmount(null);
      setUseCustomAmount(false);
    }
  };

  const getRechargeAmount = () => {
    return useCustomAmount ? parseInt(customAmount) || 0 : selectedAmount || 0;
  };

  const validateRecharge = () => {
    const amount = getRechargeAmount();
    
    if (!nfcData) {
      return { valid: false, message: 'Escanea un carnet primero' };
    }
    
    if (!amount || amount === 0) {
      return { valid: false, message: 'Selecciona o ingresa un monto' };
    }
    
    if (amount < MIN_RECHARGE) {
      return { valid: false, message: `El monto mínimo es ${formatCurrency(MIN_RECHARGE)}` };
    }
    
    if (amount > MAX_RECHARGE) {
      return { valid: false, message: `El monto máximo es ${formatCurrency(MAX_RECHARGE)}` };
    }
    
    const newBalance = nfcData.saldo + amount;
    if (newBalance > MAX_BALANCE) {
      return { valid: false, message: `El saldo no puede superar ${formatCurrency(MAX_BALANCE)}` };
    }
    
    return { valid: true };
  };

  const handleConfirmRecharge = async () => {
    const validation = validateRecharge();
    
    if (!validation.valid) {
      toast.warning(validation.message, {
        position: "top-center"
      });
      return;
    }

    const amount = getRechargeAmount();
    
    try {
      setProcessing(true);

      // Simular procesamiento
      await new Promise(resolve => setTimeout(resolve, 1500));

      const newBalance = nfcData.saldo + amount;

      // Crear transacción primero
      const transaction = {
        tipo: 'recarga',
        monto: amount,
        estudianteId: nfcData.id,
        estudianteNombre: nfcData.nombre,
        vendedorId: user.id,
        vendedorNombre: user.nombre,
        saldoAnterior: nfcData.saldo,
        saldoNuevo: newBalance,
        fecha: new Date().toISOString()
      };

      const createdTransaction = createTransaction(transaction);

      // Escribir nuevo saldo Y la transacción en NFC
      await writeNFC({ 
        ...nfcData, 
        saldo: newBalance,
        transaccion: {
          id: createdTransaction.id,
          tipo: 'recarga',
          monto: amount,
          fecha: createdTransaction.fecha,
          vendedorId: user.id
        }
      });

      toast.success(`✅ ¡Recarga exitosa! Nuevo saldo: ${formatCurrency(newBalance)}`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Limpiar formulario
      setNfcData(null);
      setNfcState(NFC_STATES.IDLE);
      setSelectedAmount(null);
      setCustomAmount('');
      setUseCustomAmount(false);

    } catch (error) {
      console.error('Error processing recharge:', error);
      toast.error('❌ Error al procesar la recarga', {
        position: "top-center"
      });
    } finally {
      setProcessing(false);
    }
  };

  const rechargeAmount = getRechargeAmount();
  const newBalance = nfcData ? nfcData.saldo + rechargeAmount : 0;

  return (
    <div className="recharge-container">
      {/* Header */}
      <header className="recharge-header">
        <div className="header-content">
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            <span className="back-icon">←</span>
            <span>Volver</span>
          </button>
          <div className="header-title">
            <div className="title-icon">💰</div>
            <div>
              <h1>Recargar Saldo</h1>
              <p>Agregar dinero al carnet NFC</p>
            </div>
          </div>
          <div className="header-badge">
            <span className="badge-icon">🏪</span>
            <span>{user?.nombre}</span>
          </div>
        </div>
      </header>

      <div className="recharge-content">
        {/* Panel NFC */}
        <div className="nfc-panel">
          <div className="panel-header">
            <span className="step-badge">Paso 1</span>
            <h2 className="panel-title">
              <span className="panel-icon">📱</span>
              Escanear Carnet
            </h2>
          </div>
          
          {nfcState === NFC_STATES.IDLE && (
            <div className="scan-area">
              <button onClick={handleScanCard} className="scan-btn">
                <div className="scan-icon-wrapper">
                  <div className="scan-rings">
                    <div className="ring ring-1"></div>
                    <div className="ring ring-2"></div>
                    <div className="ring ring-3"></div>
                  </div>
                  <span className="scan-icon">📱</span>
                </div>
                <span className="scan-text">Iniciar Escaneo NFC</span>
                <span className="scan-hint">Toca para activar el lector</span>
              </button>
            </div>
          )}

          {nfcState === NFC_STATES.SCANNING && (
            <div className="scanning-state">
              <div className="scanning-animation">
                <div className="pulse-rings">
                  <div className="pulse-ring"></div>
                  <div className="pulse-ring"></div>
                  <div className="pulse-ring"></div>
                </div>
                <div className="nfc-icon-animated">📱</div>
              </div>
              <h3>Buscando dispositivo...</h3>
              <p>Acerca el carnet al lector NFC</p>
              <div className="progress-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {nfcState === NFC_STATES.SUCCESS && nfcData && (
            <div className="student-card">
              <div className="card-header">
                <div className="student-avatar">
                  {nfcData.nombre.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                <div className="check-badge">✓</div>
              </div>
              <div className="card-body">
                <h3 className="student-name">{nfcData.nombre}</h3>
                <div className="student-details">
                  <div className="detail-item">
                    <span className="detail-icon">🎓</span>
                    <span className="detail-label">Grado:</span>
                    <span className="detail-value">{nfcData.grado}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">💰</span>
                    <span className="detail-label">Saldo actual:</span>
                    <span className="detail-value balance-amount">{formatCurrency(nfcData.saldo)}</span>
                  </div>
                </div>
              </div>
              <button onClick={handleScanCard} className="rescan-btn">
                <span>🔄</span> Escanear otro carnet
              </button>
            </div>
          )}

          {nfcState === NFC_STATES.ERROR && (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <h3>Error de lectura</h3>
              <p>No se pudo leer el carnet. Intenta nuevamente.</p>
              <button onClick={handleScanCard} className="retry-btn">
                Reintentar
              </button>
            </div>
          )}
        </div>

        {/* Panel Montos */}
        <div className="amount-panel">
          <div className="panel-header">
            <span className="step-badge">Paso 2</span>
            <h2 className="panel-title">
              <span className="panel-icon">💵</span>
              Seleccionar Monto
            </h2>
          </div>

          <div className="amounts-grid">
            {RECHARGE_AMOUNTS.map((amount) => (
              <button
                key={amount.value}
                className={`amount-card ${selectedAmount === amount.value && !useCustomAmount ? 'selected' : ''} ${!nfcData ? 'disabled' : ''}`}
                onClick={() => nfcData && handleAmountSelect(amount.value)}
                disabled={!nfcData}
              >
                <span className="amount-icon">💵</span>
                <span className="amount-value">{amount.label}</span>
                {selectedAmount === amount.value && !useCustomAmount && (
                  <div className="selected-badge">✓</div>
                )}
              </button>
            ))}
          </div>

          <div className="custom-amount-section">
            <label className="custom-label">
              <span className="label-icon">✏️</span>
              O ingresa un monto personalizado
            </label>
            <div className="custom-input-wrapper">
              <span className="currency-symbol">$</span>
              <input
                type="text"
                className={`custom-amount-input ${useCustomAmount ? 'active' : ''}`}
                placeholder="0"
                value={customAmount}
                onChange={handleCustomAmountChange}
                disabled={!nfcData}
              />
            </div>
            <p className="input-hint">
              Mínimo: {formatCurrency(MIN_RECHARGE)} - Máximo: {formatCurrency(MAX_RECHARGE)}
            </p>
          </div>
        </div>

        {/* Panel Resumen */}
        <div className="summary-panel">
          <div className="panel-header">
            <span className="step-badge">Paso 3</span>
            <h2 className="panel-title">
              <span className="panel-icon">📊</span>
              Confirmar Recarga
            </h2>
          </div>

          {!nfcData || rechargeAmount === 0 ? (
            <div className="empty-summary">
              <div className="empty-icon">💳</div>
              <h3>Sin información</h3>
              <p>Escanea un carnet y selecciona un monto para continuar</p>
            </div>
          ) : (
            <>
              <div className="summary-card">
                <div className="summary-row">
                  <span className="summary-label">
                    <span className="label-icon">💰</span>
                    Saldo actual
                  </span>
                  <span className="summary-value">{formatCurrency(nfcData.saldo)}</span>
                </div>

                <div className="summary-row highlight">
                  <span className="summary-label">
                    <span className="label-icon">➕</span>
                    Monto a recargar
                  </span>
                  <span className="summary-value recharge-amount">
                    {formatCurrency(rechargeAmount)}
                  </span>
                </div>

                <div className="summary-divider"></div>

                <div className="summary-row total">
                  <span className="summary-label">
                    <span className="label-icon">💳</span>
                    Nuevo saldo
                  </span>
                  <span className="summary-value new-balance">
                    {formatCurrency(newBalance)}
                  </span>
                </div>
              </div>

              {newBalance > MAX_BALANCE && (
                <div className="warning-message">
                  <span className="warning-icon">⚠️</span>
                  <p>El saldo superaría el límite máximo de {formatCurrency(MAX_BALANCE)}</p>
                </div>
              )}

              {rechargeAmount < MIN_RECHARGE && (
                <div className="warning-message">
                  <span className="warning-icon">⚠️</span>
                  <p>El monto mínimo de recarga es {formatCurrency(MIN_RECHARGE)}</p>
                </div>
              )}

              {rechargeAmount > MAX_RECHARGE && (
                <div className="warning-message">
                  <span className="warning-icon">⚠️</span>
                  <p>El monto máximo de recarga es {formatCurrency(MAX_RECHARGE)}</p>
                </div>
              )}

              <button
                onClick={handleConfirmRecharge}
                disabled={!validateRecharge().valid || processing}
                className="confirm-btn"
              >
                {processing ? (
                  <>
                    <div className="btn-spinner"></div>
                    <span>Procesando recarga...</span>
                  </>
                ) : (
                  <>
                    <span className="btn-icon">✓</span>
                    <span>Confirmar Recarga</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Recharge;
